import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { BRAND, errMsg, inr, today } from "./constants";

const CATS = { income: ["Rent", "Deposit", "Late checkout fee", "Food", "Laundry", "Other"], expense: ["Electricity", "Water", "Staff salary", "Repairs", "Groceries", "Internet", "Other"] };

export function LedgerPanel({ pid, authFetch, API, onChange, canDelete = true }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ kind: "income", category: "Rent", amount_inr: "", note: "", date: today() });
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/ledger` }).then((r) => setItems(r.data.items)).catch(() => {}), [pid, authFetch, API]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await authFetch({ method: "post", url: `${API}/homestay/properties/${pid}/ledger`, data: { ...f, amount_inr: +f.amount_inr, note: f.note || null } });
      toast.success(f.kind === "income" ? "Payment logged" : "Expense logged");
      setF({ ...f, amount_inr: "", note: "" }); load(); onChange();
    } catch (err) { toast.error(errMsg(err, "Could not save entry")); }
  };
  const del = async (id) => {
    try { await authFetch({ method: "delete", url: `${API}/homestay/ledger/${id}` }); load(); onChange(); }
    catch (err) { toast.error(errMsg(err, "Could not delete")); }
  };

  const cls = "rounded-xl border-2 px-2.5 py-2 text-sm w-full";
  return (
    <div data-testid="ledger-panel" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="text-sm font-bold" style={{ color: BRAND.green }}>Rent payments & expenses</div>
      <form onSubmit={add} className="mt-3 grid grid-cols-2 gap-2">
        <select data-testid="ledger-kind" className={cls} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value, category: CATS[e.target.value][0] })}>
          <option value="income">Income</option><option value="expense">Expense</option>
        </select>
        <select data-testid="ledger-category" className={cls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
          {CATS[f.kind].map((c) => <option key={c}>{c}</option>)}
        </select>
        <input required type="number" min={1} placeholder="Amount ₹" data-testid="ledger-amount" className={cls} value={f.amount_inr} onChange={(e) => setF({ ...f, amount_inr: e.target.value })} />
        <input required type="date" data-testid="ledger-date" className={cls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <input placeholder="Note (optional)" data-testid="ledger-note" className={`${cls} col-span-2`} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <button data-testid="ledger-submit" className="col-span-2 rounded-full py-2 text-sm font-bold text-white" style={{ background: f.kind === "income" ? BRAND.green : BRAND.orange }}>
          Log {f.kind}
        </button>
      </form>
      <div className="mt-3 max-h-64 overflow-y-auto divide-y" data-testid="ledger-list">
        {items.length === 0 && <div className="text-xs py-3" style={{ color: BRAND.muted }}>No entries yet.</div>}
        {items.map((it) => (
          <div key={it.id} data-testid={`ledger-row-${it.id}`} className="flex items-center justify-between py-2 text-sm">
            <div><b>{it.category}</b> <span className="text-xs" style={{ color: BRAND.muted }}>{it.date}{it.note ? ` · ${it.note}` : ""}</span></div>
            <div className="flex items-center gap-2">
              <b style={{ color: it.kind === "income" ? BRAND.green : BRAND.orange }}>{it.kind === "income" ? "+" : "−"}{inr(it.amount_inr)}</b>
              {canDelete && <button data-testid={`ledger-delete-${it.id}`} onClick={() => del(it.id)} aria-label="Delete entry"><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
